import React from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  Skeleton,
  Card,
  CardContent,
  Container,
  Grid,
  Fade,
  Backdrop
} from '@mui/material';
import {
  CloudDownload as DownloadIcon,
  Sync as SyncIcon,
  Search as SearchIcon,
  Save as SaveIcon
} from '@mui/icons-material';

// ===== ТИПЫ =====

export type LoadingType = 
  | 'spinner' 
  | 'linear' 
  | 'skeleton' 
  | 'page' 
  | 'overlay' 
  | 'inline' 
  | 'card'
  | 'table'
  | 'list';

export type LoadingSize = 'small' | 'medium' | 'large';

export interface LoadingProps {
  type?: LoadingType;
  size?: LoadingSize;
  message?: string;
  progress?: number;
  rows?: number;
  height?: number | string;
  fullPage?: boolean;
  transparent?: boolean;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}

// ===== ОСНОВНОЙ КОМПОНЕНТ =====

/**
 * 🔄 Enterprise LoadingStates
 * 
 * Унифицированная система состояний загрузки:
 * - Различные типы индикаторов
 * - Skeleton loaders для лучшего UX
 * - Прогресс-бары с процентами
 * - Полноэкранные и встроенные варианты
 */
export const LoadingState: React.FC<LoadingProps> = ({
  type = 'spinner',
  size = 'medium',
  message,
  progress,
  rows = 3,
  height = 'auto',
  fullPage = false,
  transparent = false,
  icon,
  children: _children
}) => {
  const getSizeValue = () => {
    switch (size) {
      case 'small': return 24;
      case 'medium': return 40;
      case 'large': return 60;
      default: return 40;
    }
  };

  const getSkeletonHeight = () => {
    switch (size) {
      case 'small': return 20;
      case 'medium': return 40;
      case 'large': return 60;
      default: return 40;
    }
  };

  // Спиннер
  const renderSpinner = () => (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      {icon || <CircularProgress size={getSizeValue()} />}
      {message && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {message}
        </Typography>
      )}
    </Box>
  );

  // Линейный прогресс
  const renderLinear = () => (
    <Box width="100%">
      {message && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {message}
        </Typography>
      )}
      <LinearProgress 
        variant={progress !== undefined ? "determinate" : "indeterminate"}
        {...(progress !== undefined ? { value: progress } : {})}
        sx={{ height: size === 'large' ? 8 : size === 'small' ? 4 : 6 }}
      />
      {progress !== undefined && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {Math.round(progress)}%
        </Typography>
      )}
    </Box>
  );

  // Skeleton загрузка
  const renderSkeleton = () => (
    <Box width="100%">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton
          key={index}
          variant="rectangular"
          height={getSkeletonHeight()}
          sx={{ mb: 1, borderRadius: 1 }}
          animation="wave"
        />
      ))}
    </Box>
  );

  // Карточка skeleton
  const renderCardSkeleton = () => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Box flex={1}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={20} />
          </Box>
        </Box>
        <Skeleton variant="rectangular" height={120} sx={{ mb: 2 }} />
        <Box display="flex" gap={1}>
          <Skeleton variant="rectangular" width={80} height={32} />
          <Skeleton variant="rectangular" width={100} height={32} />
        </Box>
      </CardContent>
    </Card>
  );

  // Таблица skeleton
  const renderTableSkeleton = () => (
    <Box width="100%">
      {/* Заголовок таблицы */}
      <Box display="flex" gap={2} mb={2} p={2} bgcolor="grey.50" borderRadius={1}>
        <Skeleton variant="text" width="20%" height={24} />
        <Skeleton variant="text" width="30%" height={24} />
        <Skeleton variant="text" width="25%" height={24} />
        <Skeleton variant="text" width="25%" height={24} />
      </Box>
      
      {/* Строки таблицы */}
      {Array.from({ length: rows }, (_, index) => (
        <Box key={index} display="flex" gap={2} mb={1} p={2} borderBottom="1px solid" borderColor="grey.200">
          <Skeleton variant="text" width="20%" height={20} />
          <Skeleton variant="text" width="30%" height={20} />
          <Skeleton variant="text" width="25%" height={20} />
          <Skeleton variant="text" width="25%" height={20} />
        </Box>
      ))}
    </Box>
  );

  // Список skeleton
  const renderListSkeleton = () => (
    <Box width="100%">
      {Array.from({ length: rows }, (_, index) => (
        <Box key={index} display="flex" alignItems="center" mb={2}>
          <Skeleton variant="circular" width={32} height={32} sx={{ mr: 2 }} />
          <Box flex={1}>
            <Skeleton variant="text" width="70%" height={20} />
            <Skeleton variant="text" width="50%" height={16} />
          </Box>
          <Skeleton variant="rectangular" width={60} height={24} />
        </Box>
      ))}
    </Box>
  );

  // Полноэкранная загрузка
  const renderPageLoading = () => (
    <Container maxWidth="md" sx={{ mt: 8 }}>
      <Box display="flex" flexDirection="column" alignItems="center" gap={4}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          {icon || <CircularProgress size={60} />}
          <Typography variant="h5" color="text.secondary">
            {message || 'Загрузка...'}
          </Typography>
        </Box>
        
        {/* Skeleton контент */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {Array.from({ length: 6 }, (_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                <CardContent>
                  <Skeleton variant="rectangular" height={120} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="80%" height={24} />
                  <Skeleton variant="text" width="60%" height={20} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );

  // Встроенная загрузка
  const renderInlineLoading = () => (
    <Box 
      display="flex" 
      alignItems="center" 
      gap={1}
      sx={{ 
        color: 'text.secondary',
        fontSize: size === 'small' ? '0.875rem' : '1rem'
      }}
    >
      <CircularProgress size={getSizeValue() * 0.6} />
      {message && <Typography variant="inherit">{message}</Typography>}
    </Box>
  );

  // Overlay загрузка
  const renderOverlay = () => (
    <Backdrop
      open={true}
      sx={{ 
        color: '#fff', 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: transparent ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.5)'
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
        {icon || <CircularProgress color="inherit" size={getSizeValue()} />}
        {message && (
          <Typography variant="h6" color="inherit">
            {message}
          </Typography>
        )}
        {progress !== undefined && (
          <Box width={200}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.3)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'white'
                }
              }} 
            />
            <Typography variant="caption" color="inherit" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              {Math.round(progress)}%
            </Typography>
          </Box>
        )}
      </Box>
    </Backdrop>
  );

  // Выбор типа загрузки
  const renderContent = () => {
    switch (type) {
      case 'linear': return renderLinear();
      case 'skeleton': return renderSkeleton();
      case 'card': return renderCardSkeleton();
      case 'table': return renderTableSkeleton();
      case 'list': return renderListSkeleton();
      case 'page': return renderPageLoading();
      case 'inline': return renderInlineLoading();
      case 'overlay': return renderOverlay();
      case 'spinner':
      default: return renderSpinner();
    }
  };

  // Обертка для контейнера
  const content = renderContent();

  if (type === 'page' || type === 'overlay') {
    return content;
  }

  if (fullPage) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ bgcolor: transparent ? 'transparent' : 'background.default' }}
      >
        <Fade in={true} timeout={300}>
          <Box>{content}</Box>
        </Fade>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{ 
        height: height,
        p: type === 'inline' ? 0 : 2
      }}
    >
      <Fade in={true} timeout={300}>
        <Box width="100%">{content}</Box>
      </Fade>
    </Box>
  );
};

// ===== ПРЕДУСТАНОВЛЕННЫЕ КОМПОНЕНТЫ =====

export const PageLoading: React.FC<{ message?: string }> = ({ message }) => (
  <LoadingState type="page" {...(message != null ? { message } : {})} />
);

export const CardLoading: React.FC<{ rows?: number }> = ({ rows }) => (
  <LoadingState type="card" {...(rows != null ? { rows } : {})} />
);

export const TableLoading: React.FC<{ rows?: number }> = ({ rows }) => (
  <LoadingState type="table" {...(rows != null ? { rows } : {})} />
);

export const ListLoading: React.FC<{ rows?: number }> = ({ rows }) => (
  <LoadingState type="list" {...(rows != null ? { rows } : {})} />
);

export const InlineLoading: React.FC<{ message?: string; size?: LoadingSize }> = ({ message, size }) => (
  <LoadingState type="inline" {...({ ...(message != null ? { message } : {}), ...(size != null ? { size } : {}) })} />
);

export const OverlayLoading: React.FC<{ message?: string; progress?: number; transparent?: boolean }> = ({ 
  message, 
  progress, 
  transparent 
}) => (
  <LoadingState type="overlay" {...({ ...(message != null ? { message } : {}), ...(progress != null ? { progress } : {}), ...(transparent != null ? { transparent } : {}) })} />
);

// Специализированные загрузки с иконками
export const DownloadLoading: React.FC<{ message?: string; progress?: number }> = ({ message, progress }) => (
  <LoadingState 
    type="linear" 
    message={message || 'Загрузка файла...'} 
    {...(progress != null ? { progress } : {})}
    icon={<DownloadIcon />}
  />
);

export const SyncLoading: React.FC<{ message?: string }> = ({ message }) => (
  <LoadingState 
    type="spinner" 
    message={message || 'Синхронизация...'} 
    icon={<SyncIcon className="animate-spin" />}
  />
);

export const SearchLoading: React.FC<{ message?: string }> = ({ message }) => (
  <LoadingState 
    type="inline" 
    message={message || 'Поиск...'} 
    icon={<SearchIcon />}
    size="small"
  />
);

export const SaveLoading: React.FC<{ message?: string }> = ({ message }) => (
  <LoadingState 
    type="inline" 
    message={message || 'Сохранение...'} 
    icon={<SaveIcon />}
    size="small"
  />
);

export default LoadingState;
